/* Wes's personal offer on the emailed artwork page (2026-10-17).
 *
 * 2026-10-18 (Wes): the code is an EXTRA % off the sale price, emailed 5 days after the customer first opens their
 * artwork (src/delayedOffer.js). The section passes the compare-at total and the final total to total(), so the
 * headline states the whole saving for the selected piece ("30% off" on a 16x24 framed canvas) and "You save"
 * matches it; with no compare-at it says "an extra 10% off".
 *
 * The pipeline (src/offerCode.js) makes a one-off Shopify discount code for a free-preview customer and the Worker's
 * /link-status hands it to the page as { code, percent, expiresAt }. This file only DISPLAYS what Shopify will do:
 *   - a headline with a live countdown ("6 days left"), recomputed on every visit and each minute while open;
 *   - the discounted price in the total (the full price struck through, "You save $x (20%)");
 *   - the code attached to the checkout step so it applies without typing.
 * The code itself takes the money off and stops working at its end time, whatever this page shows.
 *
 * It lives in an asset (not in sections/artwork-preview.liquid) because that section is at Shopify's 256 KB limit.
 * The section calls window.WKDOffer.init(...) lazily and only through three small hooks: set the offer, discount
 * the total, and build the checkout URL. All wording is in STRINGS below.
 *
 * ES5 on purpose (old iPhones). Never throws into the page: every entry point is guarded by the caller's checks.
 */
(function () {
  'use strict';

  var STRINGS = {
    heading: 'A personal offer from Wes: [total]% off',
    headingExtra: 'A personal offer from Wes: an extra [percent]% off',
    days: '[n] days left',
    oneDay: '1 day left',
    lastDay: 'Last day: ends tonight at 11:59 PM Central',
    ended: 'My extra [percent]% offer ended on [date].'
  };
  var CSS =
    '.apv2-offer{position:relative;margin:0 0 16px;padding:12px 16px 12px 20px;background:var(--obsidian);border:1px solid var(--chrome-dim);font-family:var(--font-ui);color:var(--ivory);line-height:1.35}' +
    '.apv2-offer[hidden]{display:none}' +
    '.apv2-offer::before{content:"";position:absolute;top:-1px;bottom:-1px;left:-1px;width:3px;background:var(--sunset-grad-v)}' +
    '.apv2-offer-h{display:block;font-size:var(--fs-min);font-weight:700}' +
    '.apv2-offer-t{display:block;margin-top:3px;font-size:var(--fs-min);font-weight: var(--fw-min);color:var(--chrome-ice)}' +
    '.apv2-offer.is-ended{opacity:.8}' +
    '.apv2-offer.is-ended::before{background:var(--chrome-border)}' +
    '.apv2-offer--card{margin:14px 0 0}';

  function addStyles() {
    if (document.getElementById('wkd-offer-css')) return;
    var st = document.createElement('style');
    st.id = 'wkd-offer-css';
    st.appendChild(document.createTextNode(CSS));
    document.head.appendChild(st);
  }

  window.WKDOffer = {
    // opts: { root, tz, rerender }
    init: function (opts) {
      var root = opts.root;
      var tz = opts.tz || 'America/Chicago';
      var rerender = opts.rerender || function () {};
      var state = null; // { code, pct, ends }
      var compareWasOff = null;
      var timer = null;
      addStyles();

      function active() {
        return !!state && Date.now() < state.ends;
      }
      function dayOf(ms) {
        try {
          return new Date(ms).toLocaleDateString('en-CA', { timeZone: tz });
        } catch (e) {
          return new Date(ms).toISOString().slice(0, 10);
        }
      }
      function dateLabel(ms) {
        try {
          return new Date(ms).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: tz });
        } catch (e) {
          return new Date(ms).toDateString();
        }
      }
      function fill(text, n) {
        return String(text)
          .split('[total]').join(String(state.tot))
          .split('[percent]').join(String(state.pct))
          .split('[n]').join(String(n == null ? '' : n))
          .split('[date]').join(dateLabel(state.ends));
      }
      // Calendar days left in Wes's time zone: "7 days left" on the day the email arrives, "Last day" on the
      // final one, however late in the day the customer looks.
      function daysLeft() {
        return Math.round((Date.parse(dayOf(state.ends) + 'T00:00:00Z') - Date.parse(dayOf(Date.now()) + 'T00:00:00Z')) / 86400000);
      }

      function render() {
        var els = root.querySelectorAll('[data-offer]');
        var box = root.querySelector('.apv2-totalbox');
        if (box && compareWasOff === null) compareWasOff = box.classList.contains('is-compare-off');
        var i;
        if (!state) {
          for (i = 0; i < els.length; i++) els[i].hidden = true;
          return;
        }
        var on = active();
        var head;
        var time = '';
        if (on) {
          var left = daysLeft();
          head = fill(state.tot ? STRINGS.heading : STRINGS.headingExtra);
          if (left >= 2) time = fill(STRINGS.days, left) + ' · ends ' + dateLabel(state.ends);
          else if (left === 1) time = fill(STRINGS.oneDay, 1) + ' · ends tomorrow (' + dateLabel(state.ends) + ')';
          else time = fill(STRINGS.lastDay);
        } else {
          head = fill(STRINGS.ended);
        }
        for (i = 0; i < els.length; i++) {
          var el = els[i];
          el.textContent = '';
          var h = document.createElement('span');
          h.className = 'apv2-offer-h';
          h.textContent = head;
          el.appendChild(h);
          if (time) {
            var t = document.createElement('span');
            t.className = 'apv2-offer-t';
            t.textContent = time;
            el.appendChild(t);
          }
          el.classList.toggle('is-ended', !on);
          // Once ended, the notice appears only beside the price, not at the top of the page.
          el.hidden = !on && el.className.indexOf('apv2-offer--top') !== -1;
        }
        // The sale price (was / now / You save) shows while the offer runs, even where the page hides compare-at prices.
        if (box) box.classList.toggle('is-compare-off', !!compareWasOff && !on);
      }

      return {
        active: active,
        // o = the Worker's answer: { code, percent, expiresAt }. Anything malformed is ignored.
        set: function (o) {
          if (!o || typeof o !== 'object') return;
          var ends = Date.parse(o.expiresAt);
          var pct = Number(o.percent);
          if (!/^[A-Za-z0-9-]{4,40}$/.test(String(o.code || '')) || !(pct >= 1 && pct <= 90) || isNaN(ends)) return;
          state = { code: String(o.code), pct: Math.round(pct), ends: ends };
          render();
          rerender();
          if (!timer) {
            timer = window.setInterval(function () {
              var was = active();
              render();
              if (was && !active()) rerender();
            }, 60000);
          }
        },
        // The price in cents after the offer, rounded the way Shopify rounds a percentage discount.
        apply: function (cents) {
          return active() ? Math.round((cents * (100 - state.pct)) / 100) : cents;
        },
        // listCents = the order at compare-at prices, finalCents = after the code. Keeps the headline's
        // percentage the same whole saving "You save" shows for the selected piece.
        total: function (listCents, finalCents) {
          var tot = listCents > finalCents && listCents > 0 ? Math.round((1 - finalCents / listCents) * 100) : 0;
          if (state && tot !== state.tot) {
            state.tot = tot;
            render();
          }
        },
        checkout: function () {
          return active() ? '/checkout?discount=' + encodeURIComponent(state.code) : '/checkout';
        }
      };
    }
  };
})();
